/**
 * template-sections.js — de onderdelen van een boring sjabloon en hun velden.
 *
 * Eén beschrijving voor de editor (welk invoerveld) en het overzicht (welke samenvatting), zodat
 * die twee nooit uit elkaar lopen. De keuzes komen uit dezelfde @mv/models-lijsten als de
 * digitaliseerformulieren: een nieuwe optie daar verschijnt hier vanzelf.
 *
 * Wat er in `defaults` wordt opgeslagen is ongewijzigd ten opzichte van de oude editor (dezelfde
 * sleutels, codes en 'ja'/'nee'/'true'/'false'-waarden): de veldapp en de backend lezen dit.
 *
 * Soorten velden (`kind`):
 *  - select  keuzelijst; `options` [{ value, label, group? }]
 *  - search  doorzoekbare keuzelijst (lange lijsten, bv. boorsysteem)
 *  - seg     gesegmenteerde knop 'Geen default | …'; `options` [{ value, label }]
 *  - toggle  aan/uit; opgeslagen als true, uit = weg
 *  - text    vrije tekst
 *  - number  getal, optioneel met `unit`
 */
import {
  COORDINATE_SYSTEMS, HEIGHT_DATUMS, SELECTABLE_SOIL_STANDARDS,
  REFERENCE_PLANES, BORING_TYPE, PEILBUIS_MATERIAAL, PEILBUIS_REFERENTIEVLAK, nen6693,
  MONSTER_VERPAKKING,
  WATERMONSTER_MATRIX, WATERMONSTER_POMPTYPE, WATERMONSTER_POMPTYPE_ALIASES, WATERBOTTLE_TYPE, WATERBOTTLE_FILTERTYPE,
} from '@mv/models';

const fromDefs = (defs) => defs.map((d) => ({ value: d.id, label: d.label }));
const fromCodes = (list) => list.map((d) => ({ value: d.code, label: d.label }));
const fromMap = (map) => [...map].map(([label, code]) => ({ value: code, label }));
const fromList = (list) => list.map((v) => ({ value: v, label: v }));
const JA_NEE = [{ value: 'ja', label: 'Ja' }, { value: 'nee', label: 'Nee' }];

/** Oudere sjablonen bewaarden ja/nee soms als boolean. */
const jaNee = (v) => (v === true ? 'ja' : v === false ? 'nee' : v);

export const TEMPLATE_SECTIONS = [
  {
    id: 'project', title: 'Project',
    desc: 'Centrale instellingen van een project, vooraf ingevuld bij het aanmaken. Handig als je vrijwel altijd in hetzelfde coördinaat- en hoogtesysteem werkt.',
    groups: [{ fields: [
      { k: 'coordinate_system', label: 'Coördinaatsysteem', kind: 'select', options: fromDefs(Object.values(COORDINATE_SYSTEMS)) },
      { k: 'height_datum', label: 'Hoogtesysteem', kind: 'select', options: fromDefs(Object.values(HEIGHT_DATUMS)) },
      { k: 'soil_standard', label: 'Standaard boorprofiel', kind: 'select', options: fromDefs(SELECTABLE_SOIL_STANDARDS) },
      { k: 'werksoort', label: 'Werksoort onderzoek', kind: 'select', options: fromCodes(nen6693.WERKSOORT) },
    ] }],
  },
  {
    id: 'boring', title: 'Boring',
    desc: 'Waarden die bij (bijna) elke boring hetzelfde zijn, zodat de veldwerker ze niet per boring hoeft in te tikken. Een nieuwe boring krijgt ze, in de veldapp, in de backoffice en via spraak. Wat de veldwerker zelf kiest of inspreekt gaat altijd voor.',
    groups: [{ fields: [
      { k: 'type', label: 'Type boring', kind: 'select', options: fromCodes(BORING_TYPE) },
      { k: 'reference_plane', label: 'Referentievlak', kind: 'select', options: fromDefs(Object.values(REFERENCE_PLANES)) },
      { k: 'boormeester', label: 'Boormeester', kind: 'text', wide: true,
        placeholder: 'Standaard: voor- en achternaam van de gebruiker',
        hint: 'Leeg = automatisch de naam van de gebruiker die de boring aanmaakt. Vul een vaste tekst in om dat te overrulen.' },
    ] }],
  },
  {
    id: 'layer', title: 'Grondlaag',
    desc: 'Bepaal hoe het laagformulier eruitziet: verberg velden die je niet gebruikt en zet een standaard boorsysteem. Zo blijft inspreken en invoeren overzichtelijk.',
    groups: [{ fields: [
      { k: 'hide_oil_water', label: 'Olie/water-reactie verbergen', kind: 'toggle' },
      { k: 'hide_geur', label: 'Geur en geurgradatie verbergen', kind: 'toggle' },
      { k: 'drilling_system', label: 'Boorsysteem', kind: 'search', wide: true, options: fromCodes(nen6693.BOORSYSTEEM) },
    ] }],
  },
  {
    id: 'monster', title: 'Monster',
    desc: 'Standaardwaarden voor een grondmonster (potje): de verpakking en of het geroerd of ongeroerd is. Dat scheelt een keuze bij elk potje.',
    groups: [{ fields: [
      { k: 'packing_type', label: 'Standaard verpakking', kind: 'select',
        options: MONSTER_VERPAKKING.map((v) => ({ value: v.code, label: v.label, group: v.group })) },
      { k: 'geroerd', label: 'Standaard geroerd', kind: 'seg',
        options: [{ value: 'true', label: 'Geroerd' }, { value: 'false', label: 'Ongeroerd' }],
        read: (v) => (v === true ? 'true' : v === false ? 'false' : v) },
    ] }],
  },
  {
    id: 'peilbuis', title: 'Peilbuis',
    desc: 'Eigenschappen van de peilbuis die je meestal hetzelfde plaatst, zoals materiaal en diameters. Zo hoef je die niet telkens opnieuw in te vullen.',
    groups: [{ fields: [
      { k: 'material', label: 'Materiaal', kind: 'select', options: fromMap(PEILBUIS_MATERIAAL) },
      { k: 'referencePointType', label: 'Ref.vlak filter', kind: 'select', options: fromMap(PEILBUIS_REFERENTIEVLAK),
        short: (l) => `Filter t.o.v. ${l.toLowerCase()}` },
      { k: 'diameterMm', label: 'Diameter buiten', kind: 'number', unit: 'mm', short: (l) => `Ø buiten ${l} mm` },
      { k: 'innerDiameterMm', label: 'Diameter binnen', kind: 'number', unit: 'mm', short: (l) => `Ø binnen ${l} mm` },
      { k: 'filterSieveUsed', label: 'Filterkous', kind: 'seg', options: JA_NEE, read: jaNee },
      { k: 'unwatered', label: 'Leeggezogen', kind: 'seg', options: JA_NEE, read: jaNee },
    ] }],
  },
  {
    id: 'watermonster', title: 'Watermonster',
    desc: 'Wat bij een watermonster meestal hetzelfde is: wie bemonstert, met welke pomp en welke flessen. Zo vult de veldwerker dat niet bij elk monster opnieuw in.',
    groups: [
      { title: 'Kern', fields: [
        { k: 'sampler', label: 'Monsternemer', kind: 'text', placeholder: 'laat leeg voor de ingelogde gebruiker',
          hint: 'Leeg laten vult automatisch de naam van de gebruiker die het monster aanmaakt. Vul alleen iets in als er altijd dezelfde vaste bemonsteraar op moet staan.' },
        { k: 'matrix', label: 'Matrix', kind: 'select', options: fromList(WATERMONSTER_MATRIX) },
      ] },
      { title: 'Grondwater', fields: [
        { k: 'pumpType', label: 'Pomptype', kind: 'select', options: fromList(WATERMONSTER_POMPTYPE),
          read: (v) => WATERMONSTER_POMPTYPE_ALIASES.get(v) ?? v },
        { k: 'aerated', label: 'Belucht', kind: 'seg', options: JA_NEE, read: jaNee },
        { k: 'gwsReferencePoint', label: 'Ref.vlak GWS', kind: 'select', options: fromMap(PEILBUIS_REFERENTIEVLAK),
          short: (l) => `GWS t.o.v. ${l.toLowerCase()}` },
      ] },
      { title: 'Flessen', fields: [
        { k: 'bottleType', label: 'Flestype', kind: 'select', options: fromList(WATERBOTTLE_TYPE) },
        { k: 'filtered', label: 'Gefiltreerd', kind: 'seg', options: JA_NEE, read: jaNee },
        { k: 'filterType', label: 'Filtertype', kind: 'select', options: fromList(WATERBOTTLE_FILTERTYPE) },
      ] },
    ],
  },
  {
    id: 'afwerking', title: 'Afwerking',
    descPre: 'Hoe het boorgat na afloop wordt gedicht, als percentages van ', descBold: 'boven naar beneden',
    descPost: '. Geef boringen met en zonder peilbuis apart op; bij het digitaliseren rekent de software dit om naar cm-trajecten.',
    groups: [],
  },
];

export const AFWERKING_PROFILES = [['zonderPeilbuis', 'Zonder peilbuis'], ['metPeilbuis', 'Met peilbuis']];

export const FILL_MATERIALS = nen6693.VULMATERIAAL;

/**
 * Kleur per vulmateriaal in de afwerkingsbalk. Niet de texturen van het boorprofiel: die zijn
 * een lichte ondergrond onder een patroon, en als effen vlak is filtergrind dan bijna wit.
 * Hier moeten de lagen naast elkaar te onderscheiden zijn: afdichting grijs/groen, filter- en
 * aanvulmateriaal zandkleurig, grond bruin.
 */
const FILL_COLORS = {
  VUL_BS: '#8c98a4', VUL_BK: '#6f7f70', VUL_BP: '#7e8c7f', VUL_CE: '#a9aeb4', VUL_KL: '#9c8f7a',
  VUL_FG: '#d8c79e', VUL_FZ: '#eadfc0', VUL_AZ: '#e3d3a8', VUL_AG: '#bdb3a3',
  VUL_UG: '#a57b53', VUL_OP: '#f1f3f6',
};

export function fillColor(code) {
  if (!code) return null;
  return FILL_COLORS[code] || '#dde1e6';
}

/** De waarde van een veld zoals de editor hem toont (oude opslagvormen omgezet). */
export function fieldValue(defaults, sectionId, f) {
  const raw = defaults?.[sectionId]?.[f.k];
  if (f.kind === 'toggle') return !!raw;
  const v = f.read ? f.read(raw) : raw;
  return v == null ? '' : String(v);
}

export function isFieldSet(defaults, sectionId, f) {
  return f.kind === 'toggle' ? fieldValue(defaults, sectionId, f) : fieldValue(defaults, sectionId, f) !== '';
}

const fieldsOf = (sec) => sec.groups.flatMap((g) => g.fields);

/** Hoeveel velden van een onderdeel er een default hebben (afwerking: profielen met trajecten). */
export function sectionCounts(defaults, sec) {
  if (sec.id === 'afwerking') {
    return { set: AFWERKING_PROFILES.filter(([w]) => (defaults?.afwerking?.[w] || []).length).length, total: AFWERKING_PROFILES.length };
  }
  const fs = fieldsOf(sec);
  return { set: fs.filter((f) => isFieldSet(defaults, sec.id, f)).length, total: fs.length };
}

/** Leesbare samenvatting van een onderdeel, voor het overzicht ('' = niets ingesteld). */
export function sectionSummary(defaults, sec) {
  if (sec.id === 'afwerking') return '';
  const parts = [];
  for (const f of fieldsOf(sec)) {
    if (!isFieldSet(defaults, sec.id, f)) continue;
    const v = fieldValue(defaults, sec.id, f);
    if (f.kind === 'toggle') { parts.push(f.label); continue; }
    const label = f.options?.find((o) => o.value === v)?.label ?? v;
    if (f.short) parts.push(f.short(label));
    else if (f.kind === 'seg') parts.push(`${f.label}: ${label.toLowerCase()}`);
    else if (f.kind === 'text') parts.push(`${f.label}: ${label}`);
    else parts.push(label);
  }
  return parts.join(' · ');
}

/** Gekleurde blokken van een afwerkingsprofiel (van boven naar beneden), voor de balk. */
export function profileSegments(rows) {
  return (rows || [])
    .filter((r) => r.van !== '' && r.van != null && r.tot !== '' && r.tot != null && Number(r.tot) > Number(r.van))
    .map((r) => {
      const a = Math.max(0, Math.min(100, Number(r.van)));
      const b = Math.max(0, Math.min(100, Number(r.tot)));
      return { start: a, size: b - a, color: fillColor(r.materiaal) || '#dde1e6' };
    });
}

/** Valideer een afwerkingsprofiel: van<tot per rij, aansluitend, samen 0–100% dekkend. */
export function validateProfile(rows) {
  const list = rows || [];
  if (!list.length) return { ok: false, msg: 'Vul de trajecten in (van–tot %, materiaal).' };
  const filled = (x) => x !== '' && x != null;
  if (!list.every((r) => filled(r.van) && filled(r.tot) && r.materiaal)) return { ok: false, msg: 'Vul elke rij volledig in (van, tot, materiaal).' };
  for (const r of list) if (Number(r.van) >= Number(r.tot)) return { ok: false, msg: 'Elke "van" moet kleiner zijn dan "tot".' };
  if (Number(list[0].van) !== 0) return { ok: false, msg: 'Begin bij 0%.' };
  for (let i = 0; i < list.length - 1; i++) {
    if (Number(list[i].tot) !== Number(list[i + 1].van)) return { ok: false, msg: 'Trajecten moeten op elkaar aansluiten (geen gat/overlap).' };
  }
  if (Number(list[list.length - 1].tot) !== 100) return { ok: false, msg: 'Moet doorlopen tot 100%.' };
  return { ok: true, msg: '✓ Sluit aan en dekt 0–100%.' };
}
